import { forwardRef } from "react";
import type { HTMLAttributes, LabelHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

export type LabelSize = "md" | "sm";
export type LabelEmphasis = "medium" | "semibold";
export type LabelSpacing = "none" | "tight" | "field" | "relaxed";

const SIZE_CLASS: Record<LabelSize, string> = {
  md: "text-sm",
  sm: "text-xs",
};

const EMPHASIS_CLASS: Record<LabelSize, Record<LabelEmphasis, string>> = {
  md: {
    medium: "font-medium text-surface-700",
    semibold: "font-semibold text-surface-900",
  },
  sm: {
    medium: "font-medium text-surface-600",
    semibold: "font-semibold text-surface-900",
  },
};

const SPACING_CLASS: Record<LabelSpacing, string> = {
  none: "",
  tight: "mb-1",
  field: "mb-1.5",
  relaxed: "mb-2",
};

export interface LabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  size?: LabelSize;
  emphasis?: LabelEmphasis;
  spacing?: LabelSpacing;
  /** Use `p` or `span` for group captions not tied to a single control. */
  as?: "label" | "p" | "span";
}

const Label = forwardRef<HTMLLabelElement, LabelProps>(
  (
    {
      className,
      size = "md",
      emphasis = "medium",
      spacing = "tight",
      as: Component = "label",
      ...props
    },
    ref,
  ) => {
    const typography = cn(
      SIZE_CLASS[size],
      EMPHASIS_CLASS[size][emphasis],
      SPACING_CLASS[spacing],
      Component === "label" && "block",
      className,
    );

    if (Component === "label") {
      return (
        <label ref={ref} className={typography} {...props} />
      );
    }

    if (Component === "p") {
      const { htmlFor: _htmlFor, form: _form, ...rest } = props;
      return (
        <p
          ref={ref as React.Ref<HTMLParagraphElement>}
          className={typography}
          {...(rest as HTMLAttributes<HTMLParagraphElement>)}
        />
      );
    }

    const { htmlFor: _htmlFor, form: _form, ...rest } = props;
    return (
      <span
        ref={ref as React.Ref<HTMLSpanElement>}
        className={typography}
        {...(rest as HTMLAttributes<HTMLSpanElement>)}
      />
    );
  },
);

Label.displayName = "Label";

export default Label;
